import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { nodeImportService } from '../../services/nodeImport.service';
import type { ImportedNodeType } from '../../types/nodeImport.types';

export const NodeImportDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onImported?: (node: ImportedNodeType | null) => void;
}> = ({ open, onClose, onImported }) => {
  const [packageName, setPackageName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const examples: Array<{ name: string; note: string }> = [
    { name: 'n8n-nodes-base', note: 'Official base nodes (imports many reference-only node types)' },
    { name: '@n8n/n8n-nodes-langchain', note: 'LangChain nodes (community package; reference-only on import)' },
    { name: 'n8n-nodes-demio', note: 'Example community node (varies by maintainer)' },
    { name: 'n8n-nodes-baserow', note: 'Example community node (varies by maintainer)' },
  ];

  const handleImport = async () => {
    if (!packageName.trim()) {
      enqueueSnackbar('Enter an npm package name', { variant: 'error' });
      return;
    }
    setLoading(true);
    setError(null);
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    try {
      const resp = await nodeImportService.importFromNpm(packageName.trim());
      if (requestId !== requestIdRef.current) return; // dialog was closed or a newer request started
      const node = resp.job.imported_node_type_data || null;
      enqueueSnackbar('Import job completed', { variant: 'success' });
      onImported?.(node);
      onClose();
    } catch (e: any) {
      if (requestId !== requestIdRef.current) return;
      const msg = e?.response?.data?.error || e?.message || 'Import failed';
      setError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        // Allow closing even while importing, so the UI never gets "stuck dark".
        requestIdRef.current += 1; // invalidate in-flight request callbacks
        setLoading(false);
        setError(null);
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Import n8n Node (definitions)</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This imports node metadata so it can appear in the palette. For many npm packages, n8n nodes are listed as file paths,
            so they import as <b>reference-only</b> nodes with warnings (you can still see and configure them later).
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Importing large packages (like <b>n8n-nodes-base</b>) can take 1–2 minutes. You can close this dialog and keep using the app.
          </Alert>
          <TextField
            fullWidth
            label="NPM package name"
            placeholder="e.g., n8n-nodes-base"
            value={packageName}
            onChange={(e) => setPackageName(e.target.value)}
            disabled={loading}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Examples (click to fill)
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {examples.map((ex) => (
              <Chip
                key={ex.name}
                label={ex.name}
                onClick={() => setPackageName(ex.name)}
                variant={packageName.trim() === ex.name ? 'filled' : 'outlined'}
                color="primary"
              />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Tip: most n8n community packages start with <b>n8n-nodes-</b> or <b>@n8n/</b>.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            requestIdRef.current += 1;
            setLoading(false);
            setError(null);
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button onClick={handleImport} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={18} /> : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


